import React from 'react';
import PropTypes from 'prop-types';
import { useApiAutocomplete } from '../../hooks/useApiAutocomplete';
import { application } from '../../services/application';
import { formatToTitleCase } from '../../utils/helpers';
import { filterOptionsByAllowedStateCodes, toAllowedCodesKey } from '../../utils/regionUtils';
import Autocomplete from '../common/Inputs/Autocomplete';

export const CustomizableAutocomplete = (props) => {
  const {
    onChange,
    value,
    multi,
    labelKey = 'value',
    apiMethod,
    label,
    where = {},
    formatLabelCB,
    minChars = 0,
    allowedStateCodes = null,
    ...rest
  } = props;

  const whereKey = React.useMemo(() => JSON.stringify(where || {}), [where]);
  const allowedCodesKey = React.useMemo(() => toAllowedCodesKey(allowedStateCodes), [allowedStateCodes]);

  const getOptionList = React.useCallback(
    (params) => application.call(apiMethod, { ...params, where: { ...where } }),
    // whereKey captures where contents without unstable object identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiMethod, whereKey]
  );

  const {
    options,
    loading,
    handleValueChange,
    filterOptions,
    handleInputChange,
    getOptionList: fetchOptions,
  } = useApiAutocomplete({
    labelKey,
    onChange: React.useCallback((val) => onChange && onChange(val), [onChange]),
    getOptionList,
    minChars,
  });

  // Refetch when sibling scope (e.g. selected states) changes.
  React.useEffect(() => {
    if (minChars > 0) {
      return;
    }
    fetchOptions({ value: '' });
  }, [whereKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const limitedOptions = React.useMemo(
    () => filterOptionsByAllowedStateCodes(options, allowedCodesKey ? allowedStateCodes : null),
    [options, allowedCodesKey, allowedStateCodes]
  );

  const validateValueArray = () => {
    if (Array.isArray(value)) {
      return value;
    } else {
      return [];
    }
  };

  return (
    <Autocomplete
      options={limitedOptions}
      label={label}
      labelKey={labelKey}
      loading={loading}
      value={validateValueArray()}
      filterOptions={filterOptions}
      onChange={handleValueChange}
      multiple={multi}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      freeSolo
      formatLabelCB={formatLabelCB || formatToTitleCase}
      onInputChange={handleInputChange}
      {...rest}
    />
  );
};

CustomizableAutocomplete.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.any,
  multi: PropTypes.bool,
  labelKey: PropTypes.string,
  apiMethod: PropTypes.string,
  label: PropTypes.string,
  where: PropTypes.object,
  formatLabelCB: PropTypes.func,
  minChars: PropTypes.number,
  allowedStateCodes: PropTypes.arrayOf(PropTypes.string),
};
